import { AppSessionStorage } from "@/lib/appStorage";
import { IS_BROWSER } from "@/lib/browserUtils";

// After a release chunk hashes change; a tab opened before the release fails to load them (ChunkLoadError).
// Reloading fetches the fresh index.html with the new hashes. The reload is done once per path so a
// genuinely broken build cannot loop.
export const CHUNK_RELOADED_FOR_KEY = "chunkReloadedFor";

export const isChunkLoadError = (error: unknown): error is Error =>
  error instanceof Error && error.name === "ChunkLoadError";

export const currentPath = (): string => window.location.pathname + window.location.search + window.location.hash;

/** Returns true when a reload was started, false when this path was already reloaded once. */
export function reloadOnceForChunk(path: string): boolean {
  if (AppSessionStorage.getString(CHUNK_RELOADED_FOR_KEY, "") === path) return false;
  AppSessionStorage.setString(CHUNK_RELOADED_FOR_KEY, path);
  window.location.assign(path);
  return true;
}

export const clearChunkReloadMark = (): void => AppSessionStorage.remove(CHUNK_RELOADED_FOR_KEY);

/**
 * Wraps an async component loader (`() => import(...)`). Vue 2 swallows a rejected loader in production
 * (only `errorComp` is shown), so the error must be handled inside the loader itself.
 */
export function asyncComponent<T>(loader: () => Promise<T>): () => Promise<T> {
  return () =>
    loader().catch((error: unknown) => {
      if (IS_BROWSER && isChunkLoadError(error)) reloadOnceForChunk(currentPath());
      throw error;
    });
}

/** Catches ChunkLoadError from dynamic imports outside of Vue (e.g. lazy libraries). */
export function installChunkLoadErrorHandler(): void {
  window.addEventListener("unhandledrejection", event => {
    if (!isChunkLoadError(event.reason)) return;
    if (reloadOnceForChunk(currentPath())) event.preventDefault();
  });
}
